/**
 * The review queue: AI proposals waiting for a decision, defects suggested from runtime
 * failures (with their evidence), and every recent AI job with its cost. Nothing here changes
 * the backlog until a person accepts it.
 *
 * Rows: what it is and one meta line. Their actions are xs icon buttons that show on hover or
 * focus (always under a finger), the same on every section; the rest sit in a menu.
 */
import { Bot, Bug, ChevronDown, ChevronRight, Copy, Eye, Inbox, MoreHorizontal, RefreshCw, ScanSearch, Siren, Sparkles, X } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { SIGNAL_KIND_LABEL, proposalSummary } from '@orgus/protocol';
import type { AiJob, DefectSignal, Id, Proposal } from '@orgus/protocol';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  GROUP_INDENT,
  IconButton,
  Pressable,
  REVEAL_GROUP,
  RelativeTime,
  RevealRest,
  RevealSlot,
  cn,
  plural,
  toast,
} from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { acceptSignal, dismissSignal, scanSignals, startAiTask } from './data.ts';
import { JobsList } from './AiJobs.tsx';
import { decisionCounts } from './model.ts';

function Section({ title, count, action, children }: { title: string; count?: number; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col overflow-hidden rounded-card border border-border bg-surface" aria-label={title}>
      <header className="flex h-row items-center gap-2 border-b border-border-subtle pl-4 pr-2">
        <h2 className="text-ui font-medium text-fg">{title}</h2>
        {count != null && <span className="text-caption text-fg-subtle num">{count}</span>}
        <span className="ml-auto flex items-center gap-1">{action}</span>
      </header>
      {children}
    </section>
  );
}

/** A title with `code` spans (from failure messages): the backticked parts as quiet inline code (mono, regular weight, a soft fill), never raw backticks. */
export function CodeText({ text }: { text: string }) {
  const parts = text.split(/`([^`]+)`/);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <code key={i} className="rounded-control bg-hover px-1 font-mono text-caption font-normal text-fg">
            {p}
          </code>
        ) : (
          p
        ),
      )}
    </>
  );
}

const KIND_WORD: Record<Proposal['kind'], string> = { import: 'Import', draft: 'Draft', split: 'Split', edit: 'Changes', links: 'Duplicates', defect: 'Defect' };

export function ReviewQueue({
  proposals,
  signals,
  jobs,
  projectId,
  projectName,
  onReview,
  onOpenItem,
  onAiStarted,
}: {
  proposals: readonly Proposal[];
  signals: readonly DefectSignal[];
  jobs: readonly AiJob[];
  projectId: Id | null;
  projectName: (id: Id) => string;
  onReview: (id: Id) => void;
  onOpenItem: (id: Id) => void;
  onAiStarted: (jobId: Id) => void;
}) {
  const [open, setOpen] = useState<Set<Id>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const inProject = <T extends { projectId: Id | null }>(x: T) => !projectId || x.projectId === projectId;
  const waiting = proposals.filter((p) => p.status === 'open' && inProject(p));
  const sigs = signals.filter((s) => s.status === 'open' && inProject(s));
  const decided = signals.filter((s) => s.status !== 'open' && inProject(s)).slice(0, 5);
  const myJobs = jobs.filter((j) => !projectId || j.projectId === projectId || j.projectId === null);
  const oneProject = new Set(sigs.map((s) => s.projectId)).size <= 1;

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };
  const toggle = (id: Id) => setOpen((x) => (x.has(id) ? new Set([...x].filter((y) => y !== id)) : new Set([...x, id])));

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorState variant="inline" error={error} action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
      <Section
        title="Proposals to review"
        count={waiting.length}
        action={
          projectId && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ScanSearch />}
              loading={busy === 'dedupe'}
              onClick={() =>
                void act('dedupe', async () => {
                  const r = await startAiTask({ task: 'dedupe', projectId });
                  onAiStarted(r.job.id);
                  toast.success('Looking for duplicates', `In ${projectName(projectId)}, by meaning.`);
                })
              }
            >
              Find duplicates
            </Button>
          )
        }
      >
        {waiting.length ? (
          <ul data-list aria-label="Proposals to review">
            {waiting.map((p) => {
              const c = decisionCounts(p);
              return (
                <li key={p.id} className={cn(REVEAL_GROUP, 'relative border-b border-border-subtle last:border-b-0')}>
                  <Pressable data-row onClick={() => onReview(p.id)} aria-label={`Review ${KIND_WORD[p.kind].toLowerCase()}: ${p.title}`} className="flex min-h-row items-center gap-3 px-4 py-2 hover:bg-hover sm:h-row sm:py-0">
                    <Sparkles className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                    {/* The title is the primary text; the kind is a quiet word after it from 640 px up. */}
                    <span className="line-clamp-3 min-w-0 flex-1 text-ui text-fg wrap-anywhere sm:line-clamp-none sm:truncate">{p.title}</span>
                    <span className="hidden shrink-0 text-caption text-fg-subtle sm:inline" data-meta>
                      {KIND_WORD[p.kind]}
                    </span>
                    <span className="hidden text-caption text-fg-subtle md:inline" data-meta>
                      {p.items.length ? proposalSummary(p.items) : p.edits.length ? plural(p.edits.length, 'change') : plural(p.links.length, 'link')}
                      {c.accepted ? `, ${c.accepted} accepted` : ''}
                    </span>
                    <RevealRest className="w-16 text-caption text-fg-subtle">
                      <RelativeTime ts={p.createdAt} />
                    </RevealRest>
                  </Pressable>
                  <RevealSlot className="absolute inset-y-0 right-4">
                    <IconButton size="xs" label={`Review ${p.title}`} icon={<Eye />} onClick={() => onReview(p.id)} />
                  </RevealSlot>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon={<Inbox />} title="Nothing to review" description="Imports, drafts and changes the AI proposes wait here until you accept or discard them." />
        )}
      </Section>

      <Section
        title="Suggested defects"
        count={sigs.length}
        action={
          <Button variant="ghost" size="sm" icon={<RefreshCw />} loading={busy === 'scan'} onClick={() => void act('scan', async () => toast.success('Looked for failures', `${plural(await scanSignals(), 'new suggestion')}.`))}>
            Look now
          </Button>
        }
      >
        {sigs.length ? (
          <ul data-list aria-label="Suggested defects">
            {sigs.map((s) => {
              const isOpen = open.has(s.id);
              const createDefect = () =>
                void act(`d:${s.id}`, async () => {
                  const r = await acceptSignal(s.id, 'direct');
                  if (r.item) {
                    toast.success(`Defect ${r.item.key} created`, 'The evidence is attached.');
                    onOpenItem(r.item.id);
                  }
                });
              return (
                <li key={s.id} className={cn(REVEAL_GROUP, 'border-b border-border-subtle last:border-b-0')}>
                  <div data-row className="flex min-h-row-2line items-center gap-3 py-2 pl-row-x pr-4 transition-colors duration-(--duration-fast) hover:bg-hover sm:h-row-2line sm:py-0">
                    {/* The chevron centred on its 16 px slot, the icon one group step in (GROUP_INDENT). */}
                    <span className="flex shrink-0 items-center gap-2">
                      <IconButton size="xs" noTooltip className="-mx-1" aria-expanded={isOpen} label={isOpen ? 'Hide the evidence' : 'Show the evidence'} icon={isOpen ? <ChevronDown /> : <ChevronRight />} onClick={() => toggle(s.id)} />
                      <Siren className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="line-clamp-3 text-ui font-medium text-fg wrap-anywhere sm:line-clamp-none sm:truncate" title={s.title.replace(/`/g, '')}>
                        <CodeText text={s.title} />
                      </span>
                      <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
                        <span className="truncate" data-meta>
                          {SIGNAL_KIND_LABEL[s.kind]}
                          {oneProject ? '' : ` in ${projectName(s.projectId)}`}
                        </span>
                        <span className="shrink-0" data-meta>
                          <RelativeTime ts={s.at} />
                        </span>
                      </span>
                    </span>
                    <RevealSlot className="gap-1">
                    <IconButton
                      size="xs"
                      className="hidden sm:inline-flex"
                      label={`Create defect from ${s.title.replace(/`/g, '')}`}
                      icon={<Bug />}
                      loading={busy === `d:${s.id}`}
                      onClick={createDefect}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton label={`More actions for ${s.title.replace(/`/g, '')}`} icon={<MoreHorizontal />} size="xs" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          icon={<Bug />}
                          onSelect={createDefect}
                        >
                          Create defect
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          icon={<Bot />}
                          onSelect={() =>
                            void act(`a:${s.id}`, async () => {
                              const r = await acceptSignal(s.id, 'ai');
                              if (r.job) {
                                onAiStarted(r.job.id);
                                toast.success('Writing the defect', 'Its draft opens for review when it is ready.');
                              }
                            })
                          }
                        >
                          Draft with AI
                        </DropdownMenuItem>
                        <DropdownMenuItem icon={<Eye />} onSelect={() => toggle(s.id)}>
                          {isOpen ? 'Hide the evidence' : 'Show the evidence'}
                        </DropdownMenuItem>
                        <DropdownMenuItem icon={<X />} onSelect={() => void act(`x:${s.id}`, () => dismissSignal(s.id))}>
                          Dismiss
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </RevealSlot>
                  </div>
                  {isOpen && (
                    // The evidence sits one group step in, under the signal's icon.
                    <div className="pb-3 pl-row-x pr-4">
                      <div className={cn('relative', GROUP_INDENT)}>
                        <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-control border border-border-subtle bg-bg px-3 py-2 font-mono text-caption text-fg-muted">{s.evidence}</pre>
                        <Button variant="ghost" size="sm" icon={<Copy />} className="absolute right-1 top-1" onClick={() => void navigator.clipboard.writeText(s.evidence).then(() => toast.success('Copied', 'The evidence'))}>
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon={<Siren />} title="No failures to look at" description="Failures are suggested here as defects, once each." />
        )}
        {decided.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-2 border-t border-border-subtle px-4 py-2 text-caption text-fg-subtle">
            Recently decided:
            {decided.map((s) =>
              s.status === 'accepted' && s.itemId ? (
                <Pressable key={s.id} className="w-auto rounded-control hover:text-fg hover:underline" onClick={() => onOpenItem(s.itemId!)}>
                  {s.title.slice(0, 40)}
                </Pressable>
              ) : (
                <span key={s.id} className={s.status === 'dismissed' ? 'line-through' : undefined}>
                  {s.title.slice(0, 40)}
                </span>
              ),
            )}
          </p>
        )}
      </Section>

      <Section title="AI jobs" count={myJobs.length}>
        <JobsList jobs={myJobs} onReview={onReview} />
      </Section>
    </div>
  );
}
